import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Search, CheckCircle2, AlertTriangle, Activity, TrendingUp, TrendingDown, Minus, Filter, X } from "lucide-react";
import { SIDE_EFFECT_CATALOG, EFFECT_CATEGORIES, SEVERITY_SORT_ORDER } from "@/lib/sideEffectsCatalog";

/**
 * MobileEffects — 100% parity with the desktop "Side-effect log" card from
 * PatientDetailPage. Same Effect picker (with categories), same Note + char
 * counter, same Severity / Trend / Frequency selects, same "Recent effects
 * (top 5)" feed. Backed by the same POST /patients/{id}/side-effects endpoint.
 */
const SEVERITY_META = {
  mild:     { label: "Mild",     cls: "bg-emerald-100 text-emerald-700 border-emerald-200", Icon: CheckCircle2 },
  moderate: { label: "Moderate", cls: "bg-amber-100 text-amber-700 border-amber-200",       Icon: AlertTriangle },
  expected: { label: "Expected", cls: "bg-sky-100 text-sky-700 border-sky-200",             Icon: Activity },
  painful:  { label: "Painful",  cls: "bg-red-100 text-red-700 border-red-200",             Icon: AlertTriangle },
  severe:   { label: "Severe",   cls: "bg-red-100 text-red-700 border-red-200",             Icon: AlertTriangle },
};
const TREND_META = {
  improving:     { label: "Improving",    cls: "bg-emerald-100 text-emerald-700", Icon: TrendingUp },
  stable:        { label: "Stable",       cls: "bg-sky-100 text-sky-700",         Icon: Minus },
  expected:      { label: "Expected",     cls: "bg-violet-100 text-violet-700",   Icon: Activity },
  normal:        { label: "Normal",       cls: "bg-stone-100 text-stone-700",     Icon: CheckCircle2 },
  deteriorating: { label: "Deteriorating",cls: "bg-red-100 text-red-700",         Icon: TrendingDown },
  worsening:     { label: "Worsening",    cls: "bg-red-100 text-red-700",         Icon: TrendingDown },
  monitoring:    { label: "Monitoring",   cls: "bg-amber-100 text-amber-700",     Icon: Search },
};

export default function MobileEffects() {
  const [patient, setPatient] = useState(null);
  const [effects, setEffects] = useState([]);
  const [newEffect, setNewEffect] = useState("");
  const [selectedEffectId, setSelectedEffectId] = useState("");
  const [frequency, setFrequency] = useState("");
  const [severity, setSeverity] = useState("");
  const [trend, setTrend] = useState("");
  const [showAll, setShowAll] = useState(false);
  const [showAllModal, setShowAllModal] = useState(false);
  const [allSearch, setAllSearch] = useState("");
  const [allCategory, setAllCategory] = useState("all");
  const [allSortBy, setAllSortBy] = useState("recent");
  const [toast, setToast] = useState("");

  const load = async () => {
    const r = await api.get("/me/calendar");
    setPatient(r.data.patient);
    if (r.data.patient) {
      const e = await api.get(`/patients/${r.data.patient.id}/side-effects`);
      setEffects(e.data);
    }
  };
  useEffect(() => { load(); }, []);

  const pickCatalogEffect = (effId) => {
    setSelectedEffectId(effId);
    const item = effId ? SIDE_EFFECT_CATALOG.find((x) => String(x.id) === String(effId)) : null;
    if (item) {
      setSeverity(item.severity || "");
      setFrequency(item.frequency_hint || "");
    }
  };

  const addEffect = async (e) => {
    e.preventDefault();
    if (!patient) return;
    if (!newEffect.trim() && !severity && !trend && !selectedEffectId) return;
    const item = selectedEffectId ? SIDE_EFFECT_CATALOG.find((x) => String(x.id) === String(selectedEffectId)) : null;
    await api.post(`/patients/${patient.id}/side-effects`, {
      note: newEffect.slice(0, 500),
      reported_effects: [],
      severity: severity || (item ? item.severity : null),
      trend: trend || null,
      effect_id: item ? item.id : null,
      effect_name: item ? item.name : null,
      category: item ? item.category : null,
      frequency: frequency || (item ? item.frequency_hint : null),
      icon: item ? item.icon : null,
    });
    setNewEffect(""); setSelectedEffectId(""); setSeverity(""); setTrend(""); setFrequency("");
    setToast("Logged. Your care team will see this.");
    setTimeout(() => setToast(""), 3000);
    load();
  };

  return (
    <div className="max-w-md mx-auto pb-4 bg-stone-50 min-h-screen" data-testid="mobile-effects-page">
      <header className="px-5 pt-6 pb-3">
        <h1 className="font-display text-2xl font-bold text-stone-900">Side effects</h1>
        <p className="text-stone-500 text-sm">Tell your care team how you're feeling.</p>
      </header>

      <section className="px-5">
        <div className="bg-white border border-stone-200 rounded-xl shadow-soft">
          <div className="px-5 py-4 border-b border-stone-100 flex items-center justify-between">
            <h3 className="font-display font-semibold text-stone-900">Side-effect log</h3>
            {effects.length > 0 && (
              <button onClick={() => setShowAllModal(true)} data-testid="view-all-effects-btn" className="text-xs font-semibold text-brand-700 inline-flex items-center gap-1">
                All Reported Effects <Search className="w-3 h-3" />
              </button>
            )}
          </div>

          <form onSubmit={addEffect} className="p-4 border-b border-stone-100 space-y-3" data-testid="side-effect-form">
            <div>
              <label className="text-xs uppercase tracking-wide text-stone-500 font-medium block mb-1.5">Effect (optional)</label>
              <select
                data-testid="side-effect-catalog"
                value={selectedEffectId}
                onChange={(e) => pickCatalogEffect(e.target.value)}
                className="w-full px-3 py-2 rounded-lg border border-stone-300 focus:border-brand-500 focus:outline-none text-sm"
              >
                <option value="">— Free-text only —</option>
                {EFFECT_CATEGORIES.filter((c) => c.id !== "all").map((cat) => (
                  <optgroup key={cat.id} label={`${cat.icon} ${cat.label}`}>
                    {SIDE_EFFECT_CATALOG.filter((eff) => eff.category === cat.id).map((eff) => (
                      <option key={eff.id} value={eff.id}>{eff.icon} {eff.name}</option>
                    ))}
                  </optgroup>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs uppercase tracking-wide text-stone-500 font-medium block mb-1.5">Note</label>
              <textarea
                data-testid="side-effect-input"
                value={newEffect}
                onChange={(e) => setNewEffect(e.target.value.slice(0, 500))}
                placeholder="How is the patient feeling? Any new or ongoing symptoms?"
                rows={3} maxLength={500}
                className="w-full px-3 py-2 rounded-lg border border-stone-300 focus:border-brand-500 focus:ring-2 focus:ring-brand-100 focus:outline-none text-sm resize-none"
              />
              <div className="text-[11px] text-stone-400 text-right mt-1" data-testid="side-effect-char-count">{newEffect.length} / 500</div>
            </div>
            <div className="grid grid-cols-2 gap-2.5">
              <div>
                <label className="text-xs uppercase tracking-wide text-stone-500 font-medium block mb-1.5">Severity</label>
                <select data-testid="side-effect-severity" value={severity} onChange={(e) => setSeverity(e.target.value)} className="w-full px-2 py-2 rounded-lg border border-stone-300 focus:border-brand-500 focus:outline-none text-sm">
                  <option value="">— Select —</option>
                  <option value="mild">Mild</option>
                  <option value="moderate">Moderate</option>
                  <option value="expected">Expected</option>
                  <option value="painful">Painful</option>
                  <option value="severe">Severe</option>
                </select>
              </div>
              <div>
                <label className="text-xs uppercase tracking-wide text-stone-500 font-medium block mb-1.5">Trend</label>
                <select data-testid="side-effect-trend" value={trend} onChange={(e) => setTrend(e.target.value)} className="w-full px-2 py-2 rounded-lg border border-stone-300 focus:border-brand-500 focus:outline-none text-sm">
                  <option value="">— Select —</option>
                  <option value="improving">📈 Improving</option>
                  <option value="stable">➡️ Stable</option>
                  <option value="expected">Expected</option>
                  <option value="normal">Normal</option>
                  <option value="deteriorating">📉 Deteriorating</option>
                  <option value="worsening">📉 Worsening</option>
                  <option value="monitoring">🔍 Monitoring</option>
                </select>
              </div>
            </div>
            <div>
              <label className="text-xs uppercase tracking-wide text-stone-500 font-medium block mb-1.5">Frequency (optional)</label>
              <input
                data-testid="side-effect-frequency"
                value={frequency}
                onChange={(e) => setFrequency(e.target.value)}
                placeholder="e.g. 3x daily, Constant, Intermittent"
                className="w-full px-3 py-2 rounded-lg border border-stone-300 focus:border-brand-500 focus:outline-none text-sm"
              />
            </div>
            <div className="flex justify-end">
              <button data-testid="add-side-effect-btn" className="px-4 py-2 rounded-lg bg-brand-500 hover:bg-brand-700 text-white text-sm font-semibold">Add entry</button>
            </div>
          </form>

          <div className="px-5 pt-3 pb-1">
            <h4 className="text-[11px] uppercase tracking-wider text-stone-700 font-bold">Recent effects (top 5)</h4>
          </div>
          <div className="max-h-80 overflow-y-auto">
            {effects.length === 0 && <div className="p-4 text-sm text-stone-500 text-center">No entries yet.</div>}
            {(showAll ? effects : effects.slice(0, 5)).map((e) => {
              const sev = e.severity ? SEVERITY_META[e.severity] : null;
              const trd = e.trend ? TREND_META[e.trend] : null;
              return (
                <div key={e.id} className="px-5 py-3 border-b border-stone-100 last:border-0" data-testid={`side-effect-entry-${e.id}`}>
                  <div className="flex items-start gap-2.5">
                    {e.icon && <div className="text-2xl shrink-0" aria-hidden="true">{e.icon}</div>}
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-1.5 mb-1">
                        {e.effect_name && <span className="font-semibold text-stone-900 text-sm">{e.effect_name}</span>}
                        {sev && <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium border ${sev.cls}`}><sev.Icon className="w-3 h-3" />{sev.label}</span>}
                        {trd && <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium ${trd.cls}`}><trd.Icon className="w-3 h-3" />{trd.label}</span>}
                      </div>
                      {e.frequency && <div className="text-xs text-stone-600 mb-1">Frequency: <b className="text-stone-900">{e.frequency}</b></div>}
                      {e.note && <div className="text-sm text-stone-800 whitespace-pre-wrap">{e.note}</div>}
                      <div className="text-xs text-stone-500 mt-1">{new Date(e.created_at).toLocaleString()}</div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          {effects.length > 5 && (
            <div className="border-t border-stone-100 p-3 text-center">
              <button onClick={() => setShowAll((v) => !v)} className="text-xs font-semibold text-brand-700">
                {showAll ? "Show top 5 only" : `Show all (${effects.length})`}
              </button>
            </div>
          )}
        </div>
      </section>

      {toast && <div className="fixed left-1/2 -translate-x-1/2 bottom-24 px-4 py-2 rounded-full bg-stone-900 text-white text-sm shadow-lg z-40" data-testid="effect-toast">{toast}</div>}

      {showAllModal && (() => {
        const filtered = effects.filter((e) => {
          const term = allSearch.trim().toLowerCase();
          const matchesSearch = !term ||
            (e.effect_name || "").toLowerCase().includes(term) ||
            (e.note || "").toLowerCase().includes(term) ||
            (e.category || "").toLowerCase().includes(term);
          const matchesCategory = allCategory === "all" || e.category === allCategory;
          return matchesSearch && matchesCategory;
        }).sort((a, b) => {
          if (allSortBy === "severity") {
            const ra = SEVERITY_SORT_ORDER[a.severity] ?? 99;
            const rb = SEVERITY_SORT_ORDER[b.severity] ?? 99;
            if (ra !== rb) return ra - rb;
          }
          return (b.created_at || "").localeCompare(a.created_at || "");
        });
        return (
          <div className="fixed inset-0 z-50 bg-stone-900/40 backdrop-blur-sm flex items-end sm:items-center justify-center sm:p-4" data-testid="all-effects-modal">
            <div className="bg-white sm:rounded-2xl rounded-t-2xl shadow-elevated w-full sm:max-w-2xl border border-stone-200 overflow-hidden flex flex-col" style={{ maxHeight: "92vh" }}>
              <div className="px-5 py-4 border-b border-stone-100 flex justify-between items-start">
                <div>
                  <h3 className="font-display font-bold text-xl text-stone-900">All Reported Effects</h3>
                  <p className="text-sm text-stone-500 mt-0.5">{filtered.length} of {effects.length} effects shown</p>
                </div>
                <button onClick={() => setShowAllModal(false)} data-testid="close-all-effects" className="p-1.5 rounded-lg hover:bg-stone-100"><X className="w-5 h-5 text-stone-500" /></button>
              </div>
              <div className="px-5 py-3 border-b border-stone-100 space-y-3">
                <div className="relative">
                  <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
                  <input data-testid="all-effects-search" value={allSearch} onChange={(e) => setAllSearch(e.target.value)} placeholder="Search effects…" className="w-full pl-10 pr-3 py-2.5 rounded-lg border border-stone-300 focus:border-brand-500 focus:outline-none text-sm" />
                </div>
                <div className="flex items-center gap-1.5 flex-wrap">
                  <Filter className="w-3.5 h-3.5 text-stone-500" />
                  <span className="text-xs font-semibold text-stone-600 mr-1">Categories</span>
                  {EFFECT_CATEGORIES.map((c) => (
                    <button key={c.id} onClick={() => setAllCategory(c.id)} data-testid={`all-effects-cat-${c.id}`}
                      className={`text-xs px-2.5 py-1 rounded-full border ${allCategory === c.id ? "bg-brand-500 text-white border-brand-700" : "bg-white text-stone-700 border-stone-300 hover:bg-stone-50"}`}>
                      {c.icon} {c.label}
                    </button>
                  ))}
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs font-semibold text-stone-600">Sort by:</span>
                  <select data-testid="all-effects-sort" value={allSortBy} onChange={(e) => setAllSortBy(e.target.value)} className="text-xs px-2 py-1 rounded-md border border-stone-300 focus:border-brand-500 focus:outline-none">
                    <option value="recent">Most Recent</option>
                    <option value="severity">Severity</option>
                  </select>
                </div>
              </div>
              <div className="flex-1 overflow-y-auto p-4 space-y-3">
                {filtered.length === 0 && <div className="text-center text-stone-500 py-12">No effects match those filters.</div>}
                {filtered.map((e) => {
                  const sev = e.severity ? SEVERITY_META[e.severity] : null;
                  const trd = e.trend ? TREND_META[e.trend] : null;
                  return (
                    <div key={e.id} className="border border-stone-200 rounded-xl p-4 hover:border-brand-300 transition-colors" data-testid={`all-effects-card-${e.id}`}>
                      <div className="flex items-start gap-3">
                        {e.icon && <div className="text-3xl shrink-0" aria-hidden="true">{e.icon}</div>}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <h4 className="font-display font-bold text-stone-900">{e.effect_name || "Free-text entry"}</h4>
                            {sev && <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium border ${sev.cls}`}><sev.Icon className="w-3 h-3" />{sev.label}</span>}
                          </div>
                          <div className="grid grid-cols-3 gap-3 mt-3 text-sm">
                            <div><div className="text-[11px] uppercase tracking-wider text-stone-500">Date</div><div className="text-stone-800">{new Date(e.created_at).toLocaleDateString()}</div></div>
                            <div><div className="text-[11px] uppercase tracking-wider text-stone-500">Frequency</div><div className="text-stone-800">{e.frequency || "—"}</div></div>
                            <div><div className="text-[11px] uppercase tracking-wider text-stone-500">Trend</div><div className="text-stone-800">{trd ? <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs ${trd.cls}`}><trd.Icon className="w-3 h-3" />{trd.label}</span> : "—"}</div></div>
                          </div>
                          {e.note && <div className="mt-3 px-3 py-2 rounded-lg bg-stone-50 border border-stone-100 text-sm text-stone-700 italic whitespace-pre-wrap">📝 {e.note}</div>}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
}
